import { computed, makeObservable, observable } from 'mobx'

import { SyncStore } from './syncStore'
import { AdminStore } from './adminStore'
import { LabelStore } from './labelStore'
import { OrderStore } from './orderStore'
import { ProductStore } from './productStore'
import { PasswordResetStore } from './passwordResetStore'

import { UnprocessedValueError } from '../common/tsUtils'

import { DialogInfo } from '../types/dialogInfo'
import { MyProductColumnId } from '../types/myProductColumnIds'
import { AllowedIntegrationsMap } from '../types/integrationTypeUtils'

import { MoySkladAppType } from '../server/mpsklad_common/MoySkladAppType'
import { UserAuthModel } from '../server/mpsklad_core/Models/UserAuthModel'
import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'

export class Store {
  @observable
  user: UserAuthModel | null

  /**
   * Alternative to cookie auth.
   * Used for MS app, because Safari doesn't allow third party iframe cookies.
   */
  @observable
  authToken: string | null

  @observable
  readonly syncStore: SyncStore

  @observable
  readonly productStore: ProductStore

  @observable
  readonly orderStore: OrderStore

  @observable
  readonly labelStore: LabelStore

  @observable
  readonly adminStore: AdminStore

  @observable
  readonly passwordResetStore: PasswordResetStore

  @observable.ref
  dialog: DialogInfo | null

  /**
   * Ids of hidden column are stored instead of visible to automatically show future columns.
   * TODO: Move inside TableData?
   */
  @observable
  hiddenMyProductColumnIds: Set<MyProductColumnId>

  constructor() {
    makeObservable(this)

    this.user = null
    this.authToken = null

    this.syncStore = new SyncStore()
    this.productStore = new ProductStore()
    this.orderStore = new OrderStore()
    this.labelStore = new LabelStore()

    this.adminStore = new AdminStore()
    this.passwordResetStore = new PasswordResetStore()

    // TODO: UI store?
    this.dialog = null
    this.hiddenMyProductColumnIds = new Set()
  }

  @computed
  get hasAuth(): boolean {
    return this.user !== null
  }

  @computed
  get hasMoySkladApp(): boolean {
    return this.user?.moySkladAppState != null
  }

  canAddStoreAccount =
    (integrationType: IntegrationType): boolean => {
      switch (integrationType) {
        case IntegrationType.MoySklad:
          return false

        case IntegrationType.Ozon:
          return this.canAddOzonAccount

        case IntegrationType.Wildberries:
          return this.canAddWbAccount

        case IntegrationType.YandexMarket:
          return this.canAddYandexMarketAccount

        default:
          throw new UnprocessedValueError('IntegrationType', integrationType)
      }
    }

  @computed
  get canAddYandexMarketAccount(): boolean {
    if (this.user == null) {
      return false
    }

    const {moySkladAppType} = this.user

    return moySkladAppType == null
           || moySkladAppType === MoySkladAppType.Ym
           || moySkladAppType === MoySkladAppType.UzbYm
           || moySkladAppType === MoySkladAppType.OzonAndWb
  }

  @computed
  get canAddOzonAccount(): boolean {
    if (this.user == null) {
      return false
    }

    const {moySkladAppType} = this.user

    return moySkladAppType == null
           || moySkladAppType === MoySkladAppType.Ozon
           || moySkladAppType === MoySkladAppType.OzonAndWb
           || (moySkladAppType === MoySkladAppType.Mini && this.syncStore.ozonAccounts.length < 2)
  }

  @computed
  get canAddWbAccount(): boolean {
    if (this.user == null) {
      return false
    }

    const {moySkladAppType} = this.user

    return moySkladAppType == null
           || moySkladAppType === MoySkladAppType.Wb
           || moySkladAppType === MoySkladAppType.OzonAndWb
           || (moySkladAppType === MoySkladAppType.Mini && this.syncStore.wbAccounts.length < 2)
  }

  @computed.struct
  get allowedIntegrations(): AllowedIntegrationsMap /* type is required */ {
    // NOTE: Making this a method will likely prevent useMemo from working properly
    const msAppType = this.user?.moySkladAppType

    switch (msAppType) {
      case null:
      case undefined:
        return {
          [IntegrationType.Ozon]: true,
          [IntegrationType.Wildberries]: true,
          [IntegrationType.YandexMarket]: true,
          [IntegrationType.MoySklad]: true
        }

      // Account checking is only for existing accounts of Ozon and Wb - old MS app limitations were bad
      case MoySkladAppType.Ozon:
        return {
          [IntegrationType.Ozon]: true,
          [IntegrationType.Wildberries]: this.syncStore.wbAccounts.length > 0,
          [IntegrationType.YandexMarket]: false,
          [IntegrationType.MoySklad]: true
        }

      case MoySkladAppType.Wb:
        return {
          [IntegrationType.Ozon]: this.syncStore.ozonAccounts.length > 0,
          [IntegrationType.Wildberries]: true,
          [IntegrationType.YandexMarket]: false,
          [IntegrationType.MoySklad]: true
        }

      case MoySkladAppType.OzonAndWb:
        return {
          [IntegrationType.Ozon]: true,
          [IntegrationType.Wildberries]: true,
          [IntegrationType.YandexMarket]: true,
          [IntegrationType.MoySklad]: true
        }

      case MoySkladAppType.Mini:
        return {
          [IntegrationType.Ozon]: true,
          [IntegrationType.Wildberries]: true,
          [IntegrationType.YandexMarket]: false,
          [IntegrationType.MoySklad]: true
        }

      case MoySkladAppType.Ym:
      case MoySkladAppType.UzbYm:
        return {
          [IntegrationType.Ozon]: false,
          [IntegrationType.Wildberries]: false,
          [IntegrationType.YandexMarket]: true,
          [IntegrationType.MoySklad]: true
        }

      default:
        throw new UnprocessedValueError('MoySkladAppType', msAppType)
    }
  }
}